import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment';
import { connect, useDispatch } from 'react-redux';

import FavoriteButton from '../FavoriteButton';
import ErrorModal from '../ErrorModal';
import {
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
  setMultiStepModalMode,
  setReservation,
} from '../../reducers';
import Button from '../Button';
import Icon from '../Icon';
import Link from '../Link';
import Rating from '../Rating';
import ReservationButtonList from '../ReservationButtonList';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import {
  ariaLabelVenue,
  priceRangeToString,
  parameterize,
  getPageContext,
} from '../../utils';
import AmexShopSmallBuyoutNotifyButton from '../AmexShopSmallBuyoutNotifyButton';
import { BUYOUT_STATE_PAST } from '../../../../consumer_web/app/modules/components/AmexShopSmall/constants';
import CONTEXT from '../ReservationButtonList/constants';
import LocationPin from '../../svg/icons/ic_location_20x20.svg';
import GdaCircleLogo from '../../svg/icons/gda_circle_logo.svg';
import { selectIsAuthenticated } from '../../reducers/authTokenSlice';
import {
  handleMktAnalytics,
} from '../../../../consumer_web/app/modules/components/AmexMarketingUtils/AmexUtils';
import './VenueCard.scss';
import { MODAL_MODES } from '../../reducers/favoriteSlice';
import { CTA_EVENT_NAME } from '../../../../consumer_web/app/modules/components/AmexMarketingUtils/constants';
import {
  getURLSearchParams,
  setSearchParams,
} from '../../utils/searchParamUtils';
import { findSlotFromSlots } from '../../utils/bookingHelpers';
import openTockUrl from '../../utils/openTockUrl';
import TockBadge from '../TockBadge';

const VenueCard = (props) => {
  const {
    $rootScope,
    activeNotifications,
    availableServiceTypes,
    cuisine,
    currency,
    event,
    events,
    featureRecaptcha,
    gdaConciergeBooking,
    image,
    isAmexShopSmallBuyoutFlag,
    isAmexShopSmallOffersFlag,
    isDeltaWifi,
    isFavorite,
    list,
    loadNotifyModal,
    locationCode,
    locationName,
    locationSlug,
    name,
    neighborhood,
    priceRange,
    rating,
    reservations,
    selectedDate,
    selectedSeats,
    shopSmallBuyoutEnabled,
    shopSmallBuyoutVenueCardEvents,
    shopSmallBuyoutState,
    shopSmallNofityMessage,
    shopSmallVenueCardEvents,
    tagBooking,
    urlSlug,
    userIsGda,
    venueId,
    amexVenueCardEvents,
    isAmexGDA,
    setGatingError,
    forwardedRef,
    isTockInventory,
    tockBusinessPageUrl,
    tockBusinessSearchUrl,
    tockNotifyUrl,
    isAmexMktgResyCreditEligible,
    tockExperiences,
  } = props;

  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const closeModal = () => setIsOpen(false);

  let pathname = (isAmexMktgResyCreditEligible && isTockInventory) ? tockBusinessPageUrl
    : `cities/${locationSlug}/venues/${urlSlug}`;

  const hasTockExperiences = isTockInventory && (tockExperiences?.length > 0);
  if (isAmexShopSmallBuyoutFlag) {
    pathname = `cities/${locationCode}/${urlSlug}/${CONTEXT.SHOP_SMALL}`;
  }

  const { utm } = tagBooking;
  const { venue_utm: venueUtm, venue_click: venueEventName } = shopSmallVenueCardEvents;
  const { venue_click: venueBuyoutEventName, notify: notifyBuyoutEventName } = shopSmallBuyoutVenueCardEvents;

  const query = {
    date: selectedDate,
    seats: selectedSeats,
    ...(isDeltaWifi && utm),
    ...(isAmexShopSmallOffersFlag && venueUtm),
  };

  const price = priceRange ? priceRangeToString(priceRange, currency) : null;
  const searchParams = getURLSearchParams();
  const token = searchParams.get('token');
  const exclusiveId = searchParams.get('exclusiveId');
  const paramVenueId = searchParams.get('paramVenueId');
  const scrollY = searchParams.get('scrollY');
  const timerRef = useRef();
  const clickOriginContextRef = useRef(null);

  if (forwardedRef) {
    forwardedRef.current = { timerRef };
  }

  const renderNoInventoryModal = () => {
    const formatSelectedDate = moment(selectedDate, 'YYYY-MM-DD').format('LL');
    const message = (
      <TranslationProvider translationKey="no_inventory_modal_message" translationValues={{ selectedDate: formatSelectedDate }}>
        <p>
          {`This venue doesn&apos;t take reservations this far in advance. Come back closer to ${formatSelectedDate} to set a notify.`}
        </p>
      </TranslationProvider>
    );
    const title = (
      <TranslationProvider translationKey="no_inventory_modal_title">
        <p>Notify unavailable</p>
      </TranslationProvider>
    );
    const styleOverrides = {
      content: {
        padding: '20px 20px 20px',
        fontSize: '16px',
      },
    };

    return (
      <ErrorModal
        className="NoInventoryErrorModal"
        allowClickOutsideToDimiss
        message={message}
        modalStyles={styleOverrides}
        primaryCTAText={getTranslation({
          translationKey: 'ok',
          fallback: 'OK',
        })}
        primaryCTAKey="ok"
        title={title}
        onErrorModalClose={closeModal}
        primaryCTAOnClick={closeModal}
      />
    );
  };

  // future refactor move this to ReservationButtonList or ReservationButton or HOC?
  const onReservationSelect = (reservation, stId, notifyService) => {
    const { isAuthenticated, showAuthModal } = props;
    const reservationConfig = {};
    reservationConfig.venueId = venueId;
    reservationConfig.src = 'resy.com-venue-card';
    reservationConfig.date = moment(selectedDate).format('YYYY-MM-DD');
    reservationConfig.seats = selectedSeats;

    // This is added for shop small buyout. This will append a parameter to the widget url
    // File : packages/widget/app/embed.js
    if (isAmexShopSmallBuyoutFlag) {
      reservationConfig.shopSmallValue = CONTEXT.SHOP_SMALL;
    }

    if (reservation) {
      reservationConfig.tableConfigId = reservation.token;
    }

    if (reservations.length === 0 && availableServiceTypes.length === 0) {
      setIsOpen(true);
      return;
    }

    if (notifyService) {
      loadNotifyModal({
        activeNotifications,
        selectedDate,
        selectedQuantity: selectedSeats,
        notifyService,
        venueId,
        venueName: name,
        venueUrl: urlSlug,
      });
      return;
    }

    if (reservation && !reservation.isEligible) {
      if (isAuthenticated) {
        setGatingError({ ...reservation, venueName: name });
        $rootScope.$broadcast('MODAL', 'gating-error');
      } else {
        setSearchParams('token', reservation.token);
        setSearchParams('exclusiveId', reservation.exclusiveId);
        setSearchParams('paramVenueId', venueId);
        setSearchParams('scrollY', window.scrollY);
        showAuthModal();
      }
    } else {
      resyWidget.openModal(reservationConfig, reservation);
    }
  };

  useEffect(() => {
    const clearTokenAndExclusiveId = () => {
      setSearchParams('token', null);
      setSearchParams('exclusiveId', null);
      setSearchParams('paramVenueId', null);
      setSearchParams('scrollY', null);
    };

    const loginThroughGatedSlotClick = (
      reservationsParam,
      tokenParam,
      exclusiveIdParam
    ) => {
      const slotFound = findSlotFromSlots(
        reservationsParam,
        tokenParam,
        Number(exclusiveIdParam)
      );
      if (slotFound) {
        timerRef.current = setTimeout(() => {
          window.scrollTo(0, scrollY);
          onReservationSelect(slotFound, null, null);
          clearTokenAndExclusiveId();
        }, 1000);
      } else {
        timerRef.current = setTimeout(() => {
          clearTokenAndExclusiveId();
        }, 1000);
      }
    };
    if (reservations && token && Number(paramVenueId) === venueId) {
      loginThroughGatedSlotClick(reservations, token, exclusiveId);
    }

    return () => {
      // Always call clearTimeout if timerRef.current is not null or undefined
      if (timerRef.current !== undefined && timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [reservations, token]);

  const handleVenueCardClick = () => {
    if (!isAmexShopSmallBuyoutFlag) {
      const params = {
        label: 'venue details',
        category: 'Homepage Interaction',
        venue: name,
        venue_id: venueId,
        list,
        location: locationCode,
        eventAsset: `${list} ${name}`,
      };
      // MARKETING ANALYTICS START
      if (isDeltaWifi) {
        const extraParams = {
          action_name: 'Venue Click',
          item_name: name,
        };
        handleMktAnalytics(event, CTA_EVENT_NAME, extraParams);
        window.location.href = `${pathname}?${parameterize(query)}`;
      } else if (isAmexShopSmallOffersFlag) {
        const updatedParams = {
          ...params,
          category: 'Shop Small Offers Interaction',
          context: 'Shop Small Page',
        };
        event(venueEventName, updatedParams);
        window.location.href = `${pathname}?${parameterize(query)}`;
      } else if (isAmexGDA) {
        const extraParams = {
          action_name: 'GDA Book Now - Venue Click',
          item_name: name,
        };

        handleMktAnalytics(event, CTA_EVENT_NAME, extraParams);
        window.location.href = `${pathname}?${parameterize(query)}`;
      } if (isAmexMktgResyCreditEligible) {
        const extraParams = {
          action_name: isTockInventory ? 'Book Now - Tock Venue Click' : 'Book Now - Resy Venue Click',
          item_name: name,
        };
        handleMktAnalytics(event, CTA_EVENT_NAME, extraParams);

        if (isTockInventory) {
          openTockUrl(pathname, dispatch);
        } else {
          window.open(`${pathname}?${parameterize(query)}`, '_blank');
        }
      } else {
        const clickContext = clickOriginContextRef.current || getPageContext(window.location.pathname);
        event('Clicked homepage venue card', {
          ...params,
          context: clickContext || undefined,
        });
      }
    } else if (isAmexShopSmallBuyoutFlag && shopSmallBuyoutEnabled) {
      const params = {
        label: 'venue details',
        category: 'Shop Small Buyout Interaction',
        venue: name,
        venue_id: venueId,
        list,
        location: locationCode,
        context: 'Shop Small Page',
      };
      event(venueBuyoutEventName, params);
      onReservationSelect();
    }
  };

  const captureClickOriginContext = () => {
    clickOriginContextRef.current = getPageContext(window.location.pathname);
  };

  const withCollectionParam = (hrefValue) => {
    if (!list) {
      return hrefValue;
    }
    const separator = hrefValue.includes('?') ? '&' : '?';
    return `${hrefValue}${separator}collection=${encodeURIComponent(list)}`;
  };

  const toggleGdaConciergeModal = () => {
    $rootScope.$broadcast('MODAL', 'concierge-booking');
  };

  const renderButtons = () => {
    if (!name) {
      return (
        <div className="ReservationButtonList">
          <Button className="skeleton" disabled />
          <Button className="skeleton" disabled />
        </div>
      );
    }

    if (isAmexMktgResyCreditEligible && reservations.length === 0 && availableServiceTypes.length === 0 && !(isTockInventory && tockNotifyUrl)) {
      return (
        <TranslationProvider translationKey="no_availability_for_party_message" translationValues={{ partySize: selectedSeats }}>
          <div className="VenueCard__no-availability body--sm color--text-secondary">{`Sorry, we don't currently have any tables available for ${selectedSeats}.`}</div>
        </TranslationProvider>
      );
    }

    if (isAmexShopSmallBuyoutFlag && !shopSmallBuyoutEnabled) {
      return (
        <AmexShopSmallBuyoutNotifyButton
          activeNotifications={activeNotifications}
          Analytics={{ event }}
          limit={2}
          list={list}
          onReservationSelect={onReservationSelect}
          availableServiceTypes={availableServiceTypes}
          venueId={venueId}
          shopSmallNofityMessage={shopSmallNofityMessage}
          notifyBuyoutEventName={notifyBuyoutEventName}
        />
      );
    }

    const userIsNotGDAEligible = gdaConciergeBooking && !userIsGda;
    const gdaVenueHasNoShiftsOrEvents = gdaConciergeBooking
      && availableServiceTypes.length === 0
      && events.length === 0;

    if (userIsNotGDAEligible || gdaVenueHasNoShiftsOrEvents) {
      return (
        <Button
          data-testid="gda-concierge"
          onClick={toggleGdaConciergeModal}
          variant="gda-concierge"
        >
          <GdaCircleLogo className="Button--gda-concierge__logo" />
          <TranslationProvider translationKey="contact_amex_to_book">
            <span>Contact Amex to Book</span>
          </TranslationProvider>
        </Button>
      );
    }
    let contextValue = CONTEXT.HOMEPAGE;
    if (isDeltaWifi) {
      contextValue = CONTEXT.DELTA_WIFI;
    } else if (isAmexShopSmallBuyoutFlag || isAmexShopSmallOffersFlag) {
      contextValue = CONTEXT.SHOP_SMALL;
    } else if (isAmexMktgResyCreditEligible) {
      contextValue = CONTEXT.AMEX_RESYCREDITELIGIBLE;
    }
    return (
      <ReservationButtonList
        activeNotifications={activeNotifications}
        Analytics={{ event }}
        context={contextValue}
        featureRecaptcha={featureRecaptcha}
        reservations={reservations}
        limit={2}
        list={list}
        onReservationSelect={onReservationSelect}
        availableServiceTypes={availableServiceTypes}
        venueName={name}
        venueCity={locationName}
        venueId={venueId}
        amexVenueCardEvents={amexVenueCardEvents}
        shopSmallVenueCardEvents={shopSmallVenueCardEvents}
        shopSmallBuyoutVenueCardEvents={shopSmallBuyoutVenueCardEvents}
        isAmexShopSmallOffersFlag={isAmexShopSmallOffersFlag}
        isAmexShopSmallBuyoutFlag={isAmexShopSmallBuyoutFlag}
        partySize={selectedSeats}
        isTockInventory={isTockInventory}
        tockBusinessSearchUrl={tockBusinessSearchUrl}
        tockNotifyUrl={tockNotifyUrl}
        experiences={tockExperiences}
        hasTockExperiences={hasTockExperiences}
        selectedDate={selectedDate}
      />
    );
  };

  const handleHeartIconClick = () => {
    const { isAuthenticated, showAuthModal } = props;

    const showMultiListModal = () => {
      $rootScope.reduxStore.dispatch(
        setMultiStepModalVenueContext({ name, city: locationName })
      );
      $rootScope.reduxStore.dispatch(setMultiStepModalVenueId(venueId));
      $rootScope.reduxStore.dispatch(
        setMultiStepModalMode(MODAL_MODES.ADD_TO_LIST)
      );
      $rootScope.reduxStore.dispatch(setMultiStepModalOpenState(true));
    };

    if (isAuthenticated) {
      showMultiListModal();
    } else {
      showAuthModal(showMultiListModal);
    }
  };

  const renderSaveButton = () => (
    <div className="VenueCard__saveButton">
      <FavoriteButton
        height={25}
        on={isFavorite}
        onFavoriteClick={handleHeartIconClick}
        venueName={name}
        width={25}
      />
    </div>
  );

  return (
    <div className="VenueCard">
      {isAmexShopSmallBuyoutFlag && (
        <p className="VenueCard__locationName">{locationName}</p>
      )}
      <div
        alt=""
        className={cx('VenueCard__image', {
          soldout:
            isAmexShopSmallBuyoutFlag
            && shopSmallBuyoutState === BUYOUT_STATE_PAST,
        })}
        style={{
          backgroundImage: `url(${image})`,
        }}
      >
        <div
          className={cx('skeleton', {
            'no-skeleton': (name && image) || image === null,
          })}
        />
      </div>
      {name && !isTockInventory && renderSaveButton()}
      <div className="VenueCard__content">
        <div className="VenueCard__content__info">
          <Link
            onMouseDown={captureClickOriginContext}
            onTouchStart={captureClickOriginContext}
            onClick={handleVenueCardClick}
            {...(() => {
              if (isAmexShopSmallBuyoutFlag && shopSmallBuyoutEnabled) {
                return { href: `${pathname}?${parameterize(query)}` };
              }
              if (!isAmexShopSmallBuyoutFlag && !isAmexGDA && !isDeltaWifi && !isAmexMktgResyCreditEligible) {
                return { href: withCollectionParam(`${pathname}?${parameterize(query)}`) };
              }
              return {};
            })()}
            tabIndex="0"
            {...(isAmexShopSmallOffersFlag ? { target: '_blank' } : {})}
            aria-label={ariaLabelVenue(name, cuisine, neighborhood, price)}
            variant="article"
            className={cx('', {
              'disable-link':
                isAmexShopSmallBuyoutFlag
                && (shopSmallBuyoutState === BUYOUT_STATE_PAST
                  || !shopSmallBuyoutEnabled),
            })}
          >
            <h2
              className={cx('VenueCard__content__info__name skeleton', {
                'no-skeleton': name,
              })}
            >
              {name}
            </h2>
          </Link>
          {name && (
            <div className="VenueCard__attributes">
              {!isTockInventory && (
                <div className="VenueCard__content__info__rating">
                  <Rating
                    scale={5}
                    score={rating}
                    soldout={reservations.length === 0}
                  />
                  &nbsp;•&nbsp;
                </div>
              )}
              <div>
                {cuisine}
              </div>
              { price && (
              <div data-testid="venue-card-price">
                &nbsp;•&nbsp;
                {price}
              </div>
              )}
            </div>
          )}
          {neighborhood && (
            <>
              <div
                className={cx(
                  'VenueCard__content__info__neighborhood skeleton',
                  {
                    'no-skeleton': name,
                  }
                )}
              >
                <Icon component={LocationPin} name="location-pin" />
                {neighborhood}
              </div>
            </>
          )}
        </div>
        {isTockInventory && <TockBadge />}
        {!(
          isAmexShopSmallBuyoutFlag
          && shopSmallBuyoutState === BUYOUT_STATE_PAST
        ) && renderButtons()}
      </div>
      {isOpen && renderNoInventoryModal()}
    </div>
  );
};

VenueCard.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
    reduxStore: PropTypes.shape({
      dispatch: PropTypes.func.isRequired,
      getState: PropTypes.func.isRequired,
      favorite: PropTypes.shape({
        venueIds: PropTypes.func,
      }),
    }).isRequired,
  }).isRequired,
  activeNotifications: PropTypes.shape({
    [PropTypes.number]: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  availableServiceTypes: PropTypes.arrayOf(PropTypes.string),
  cuisine: PropTypes.string,
  currency: PropTypes.string,
  event: PropTypes.func,
  events: PropTypes.arrayOf(PropTypes.shape({})),
  favoriteVenueIds: PropTypes.arrayOf(PropTypes.number),
  favorite: PropTypes.bool,
  featureRecaptcha: PropTypes.bool,
  gdaConciergeBooking: PropTypes.bool,
  image: PropTypes.string,
  isAmexShopSmallBuyoutFlag: PropTypes.bool,
  isAmexShopSmallOffersFlag: PropTypes.bool,
  isAuthenticated: PropTypes.bool.isRequired,
  isDeltaWifi: PropTypes.bool,
  isFavorite: PropTypes.bool.isRequired,
  list: PropTypes.string,
  loadNotifyModal: PropTypes.func.isRequired,
  locationCode: PropTypes.string,
  locationName: PropTypes.string,
  locationSlug: PropTypes.string,
  name: PropTypes.string,
  neighborhood: PropTypes.string,
  priceRange: PropTypes.number,
  rating: PropTypes.number,
  reservations: PropTypes.arrayOf(PropTypes.object),
  selectedDate: PropTypes.string.isRequired,
  selectedSeats: PropTypes.number.isRequired,
  selectedTime: PropTypes.number,
  shopSmallBuyoutEnabled: PropTypes.bool,
  shopSmallBuyoutState: PropTypes.string,
  shopSmallNofityMessage: PropTypes.string,
  shopSmallVenueCardEvents: PropTypes.shape({
    view_more: PropTypes.string,
    book_now: PropTypes.string,
    notify: PropTypes.string,
    venue_click: PropTypes.string,
    venue_utm: PropTypes.shape({
      utm_source: PropTypes.string,
      utm_medium: PropTypes.string,
      utm_campaign: PropTypes.string,
    }),
  }),
  shopSmallBuyoutVenueCardEvents: PropTypes.shape({
    view_more: PropTypes.string,
    book_now: PropTypes.string,
    notify: PropTypes.string,
    venue_click: PropTypes.string,
  }),
  showAuthModal: PropTypes.func,
  tagBooking: PropTypes.shape({
    utm: PropTypes.shape({}),
  }),
  urlSlug: PropTypes.string.isRequired,
  userIsGda: PropTypes.bool,
  venueId: PropTypes.number.isRequired,
  amexVenueCardEvents: PropTypes.shape({
    view_more: PropTypes.string,
    book_now: PropTypes.string,
    notify: PropTypes.string,
    venue_click: PropTypes.string,
    venue_utm: PropTypes.shape({
      utm_source: PropTypes.string,
      utm_medium: PropTypes.string,
      utm_campaign: PropTypes.string,
    }),
    event_name: PropTypes.string,
    action_names: PropTypes.shape({ venue: PropTypes.string }),
    fav_icon_click: PropTypes.string,
  }),
  amexContextName: PropTypes.string,
  isAmexGDA: PropTypes.bool,
  setGatingError: PropTypes.func.isRequired,
  forwardedRef: PropTypes.shape({ current: PropTypes.instanceOf(Object) }),
  isTockInventory: PropTypes.bool,
  tockBusinessPageUrl: PropTypes.string,
  tockBusinessSearchUrl: PropTypes.string,
  tockNotifyUrl: PropTypes.string,
  isAmexMktgResyCreditEligible: PropTypes.bool,
  tockExperiences: PropTypes.arrayOf(PropTypes.object),
};

VenueCard.defaultProps = {
  activeNotifications: {},
  availableServiceTypes: [],
  cuisine: null,
  currency: '$',
  event: () => { },
  events: [],
  favoriteVenueIds: [],
  favorite: false,
  featureRecaptcha: false,
  gdaConciergeBooking: false,
  image: undefined,
  isAmexShopSmallBuyoutFlag: false,
  isAmexShopSmallOffersFlag: false,
  isDeltaWifi: false,
  list: undefined,
  locationCode: 'ny',
  locationName: 'New York',
  locationSlug: 'undefined',
  name: null,
  neighborhood: null,
  priceRange: null,
  rating: 5,
  reservations: [],
  shopSmallBuyoutEnabled: false,
  shopSmallBuyoutState: '',
  shopSmallBuyoutVenueCardEvents: {},
  shopSmallNofityMessage: '',
  shopSmallVenueCardEvents: {},
  showAuthModal: () => { },
  tagBooking: {},
  userIsGda: false,
  amexVenueCardEvents: {},
  amexContextName: '',
  selectedTime: undefined,
  isAmexGDA: false,
  forwardedRef: undefined,
  isTockInventory: false,
  tockBusinessPageUrl: '',
  tockBusinessSearchUrl: '',
  tockNotifyUrl: '',
  isAmexMktgResyCreditEligible: false,
  tockExperiences: [],
};

const mapStateToProps = (state, ownProps) => ({
  isAuthenticated: selectIsAuthenticated(state),
  isFavorite: !!state.favoriteVenues.venuesByUser[ownProps.venueId],
});

const mapDispatchToProps = (dispatch) => ({
  setGatingError: (data) => dispatch(setReservation(data)),
});

VenueCard.displayName = 'VenueCard';
// Forward ref to expose timerRef for testing
const ForwardedVenueCard = React.forwardRef((props, ref) => (
  <VenueCard {...props} forwardedRef={ref} />
));
ForwardedVenueCard.displayName = 'VenueCard';
export { ForwardedVenueCard as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(ForwardedVenueCard);
